'use client';

import { useIsClient } from '@lib/hooks/useIsClient';
import { createPortal } from 'react-dom';
import { Link } from 'ui-components';

const DRAFT_MODE_BANNER_ID = 'draft-mode-banner-container';

export const DraftModeBannerContainer = () => (
  <div id={DRAFT_MODE_BANNER_ID} data-test={DRAFT_MODE_BANNER_ID} />
);

type DraftModeBannerProps = {
  // When provided, the action link goes here (legacy URL-based /preview/[entryId] routes
  // that never set a draft-mode cookie). When omitted, the link calls /api/draft/disable
  // to clear the cookie and reload the current page.
  livePageUrl?: string;
};

const DraftModeBanner = ({ livePageUrl }: DraftModeBannerProps) => {
  const isClient = useIsClient();
  if (!isClient) return null;

  const exitHref =
    livePageUrl ??
    `/api/draft/disable?slug=${encodeURIComponent(
      `${window.location.pathname}${window.location.search}${window.location.hash}`
    )}`;
  const linkLabel = livePageUrl ? 'View live page' : 'Exit draft mode';

  const target = document.getElementById(DRAFT_MODE_BANNER_ID);
  if (!target) return null;

  // Pixel-mirrors hexagon's apps/web/components/Preview/PreviewExitBanner so editors
  // see the same surface in unity.com and Asset Store. Dimensions and colors below
  // are lifted from packages/tailwind-config/base.css and components/Button/button.css
  // in the hexagon repo (--color-blue, --color-blue-dark, btn-md sizing).
  return createPortal(
    <div
      data-test="draft-mode-banner"
      className="fixed bottom-0 left-0 right-0 z-50 flex h-[60px] items-center bg-white px-5"
    >
      <div className="flex flex-1 items-center">
        <p className="m-0 text-base text-black">
          You are in <b>Draft Mode</b>, your changes will be live once you
          publish them, be cautious.
        </p>
      </div>
      <Link
        href={exitHref}
        className="inline-block whitespace-nowrap rounded-sm bg-[#3a5bc7] px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#0a5cf5]"
      >
        {linkLabel}
      </Link>
    </div>,
    target
  );
};

export default DraftModeBanner;
